import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Container,
  Stack,
  Anchor,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { IconAt, IconLock, IconUser } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';

const registerSchema = z
  .object({
    email: z.string().email('Ungültige E-Mail-Adresse'),
    password: z.string().min(8, 'Passwort muss mindestens 8 Zeichen lang sein'),
    confirmPassword: z.string(),
    firstName: z.string().min(2, 'Vorname ist erforderlich'),
    lastName: z.string().min(2, 'Nachname ist erforderlich'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwörter stimmen nicht überein',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
    },
    validate: schemaResolver(registerSchema, { sync: true }),
  });

  const handleSubmit = async (values: RegisterFormValues) => {
    setLoading(true);
    try {
      await register({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });
      navigate('/login');
    } catch {
      // Error handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size={420} py={80}>
      <Title ta="center" fw={700}>
        Konto erstellen
      </Title>
      <Text c="vdzGray.6" size="sm" ta="center" mt={5}>
        Bereits registriert?{' '}
        <Anchor component={Link} to="/login" size="sm">
          Jetzt anmelden
        </Anchor>
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Vorname"
              placeholder="Max"
              leftSection={<IconUser size={16} />}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Nachname"
              placeholder="Mustermann"
              leftSection={<IconUser size={16} />}
              {...form.getInputProps('lastName')}
            />
            <TextInput
              label="E-Mail"
              placeholder="ihre@email.de"
              leftSection={<IconAt size={16} />}
              {...form.getInputProps('email')}
            />
            <PasswordInput
              label="Passwort"
              placeholder="Mindestens 8 Zeichen"
              leftSection={<IconLock size={16} />}
              {...form.getInputProps('password')}
            />
            <PasswordInput
              label="Passwort bestätigen"
              placeholder="Passwort wiederholen"
              leftSection={<IconLock size={16} />}
              {...form.getInputProps('confirmPassword')}
            />
            <Button type="submit" fullWidth loading={loading}>
              Registrieren
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
